"use client";

import { c as _c } from "react/compiler-runtime";
import { trackEvent } from "@justhome/common";
import type { RatePoint } from "@lib/api/interestRates";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState, type KeyboardEvent, type PointerEvent } from "react";
import { DAY_MS, DEFAULT_RANGE, VIEWBOX, valueAt, xPercent, yPercent, type RangeKey, type Tick } from "./chart";
export type Direction = "up" | "down" | "flat";
export interface PlotChange {
  direction: Direction;
  amount: string;
  since: string;
  spokenDirection: string;
}
export interface PlotView {
  label: string;
  path: string;
  endY: number;
  ticks: Tick[];
  change: PlotChange | null;
  points: RatePoint[];
  start: number;
  end: number;
  low: number;
  high: number;
}
export interface PlotLink {
  label: string;
  href?: string;
  trackingId?: string | null;
}
interface RatePlotProps {
  views: Record<RangeKey, PlotView>;
  order: RangeKey[];
  rangeGroupLabel: string;
  caption?: string | null;
  link?: PlotLink | null;
  ringClass: string;
}
const ARROWS: Record<Direction, string> = {
  up: "M8 13V3M8 3L4 7M8 3l4 4",
  down: "M8 3v10M8 13l-4-4M8 13l4-4",
  flat: "M3 8h10M13 8l-4-4M13 8l-4 4"
};
const KEY_STEPS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowDown: -1,
  ArrowRight: 1,
  ArrowUp: 1
};
const MONTH_DAYS = 30;
const tickShift = (position: number) => position < 6 ? "0" : position > 94 ? "-100%" : "-50%";
const RatePlot = t0 => {
  const $ = _c(129);
  const {
    views,
    order,
    rangeGroupLabel,
    caption,
    link,
    ringClass
  } = t0;
  const t = useTranslations("homepage-v2");
  const locale = useLocale();
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [cursor, setCursor] = useState(null);
  const view = views[range];
  let t1;
  if ($[0] !== locale) {
    t1 = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rate = t1;
  let t2;
  if ($[2] !== locale) {
    t2 = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC"
    });
    $[2] = locale;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const date = t2;
  let t3;
  if ($[4] !== view.end || $[5] !== view.start) {
    t3 = time => Math.min(view.end, Math.max(view.start, Math.round(time / DAY_MS) * DAY_MS));
    $[4] = view.end;
    $[5] = view.start;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const toDay = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = key => {
      setRange(key);
      setCursor(null);
      trackEvent("rate-chart-range-changed", {
        range: key
      });
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const select = t4;
  let t5;
  if ($[8] !== toDay || $[9] !== view.end || $[10] !== view.start) {
    t5 = event => {
      const {
        left,
        width
      } = event.currentTarget.getBoundingClientRect();
      if (width > 0) {
        const share = (event.clientX - left) / width;
        setCursor(toDay(view.start + share * (view.end - view.start)));
      }
    };
    $[8] = toDay;
    $[9] = view.end;
    $[10] = view.start;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const point = t5;
  let t6;
  if ($[12] !== cursor || $[13] !== toDay || $[14] !== view.end || $[15] !== view.start) {
    t6 = event_0 => {
      const from = cursor ?? view.end;
      const days = (KEY_STEPS[event_0.key] ?? 0) * (event_0.shiftKey ? MONTH_DAYS : 1);
      const next = event_0.key === "Home" ? view.start : event_0.key === "End" ? view.end : days ? from + days * DAY_MS : null;
      if (next !== null) {
        event_0.preventDefault();
        setCursor(toDay(next));
      }
    };
    $[12] = cursor;
    $[13] = toDay;
    $[14] = view.end;
    $[15] = view.start;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const step = t6;
  const shown = cursor ?? view.end;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t7;
  let t8;
  let t9;
  if ($[17] !== cursor || $[18] !== date || $[19] !== order || $[20] !== point || $[21] !== range || $[22] !== rangeGroupLabel || $[23] !== rate || $[24] !== ringClass || $[25] !== shown || $[26] !== step || $[27] !== t || $[28] !== view.change || $[29] !== view.end || $[30] !== view.endY || $[31] !== view.high || $[32] !== view.low || $[33] !== view.path || $[34] !== view.points || $[35] !== view.start || $[36] !== views) {
    const shownRate = valueAt(view.points, shown);
    let t27;
    if ($[57] !== shown || $[58] !== view.end || $[59] !== view.start) {
      t27 = xPercent(shown, view.start, view.end);
      $[57] = shown;
      $[58] = view.end;
      $[59] = view.start;
      $[60] = t27;
    } else {
      t27 = $[60];
    }
    const x = t27;
    const y = yPercent(shownRate, view.low, view.high);
    t25 = "flex flex-col gap-s p-3xs m:p-s min-w-none";
    let t28;
    if ($[61] !== order || $[62] !== range || $[63] !== views) {
      let t29;
      if ($[65] !== range || $[66] !== views) {
        t29 = key_0 => <button key={key_0} type="button" aria-pressed={key_0 === range} onClick={() => select(key_0)} className={`min-h-[36px] px-3xs rounded-xs text-title-1 font-semibold focusable transition-colors ${key_0 === range ? "bg-neutral-0 text-neutral-675 shadow-4" : "text-neutral-425 hover:text-neutral-675"}`}>{views[key_0].label}</button>;
        $[65] = range;
        $[66] = views;
        $[67] = t29;
      } else {
        t29 = $[67];
      }
      t28 = order.map(t29);
      $[61] = order;
      $[62] = range;
      $[63] = views;
      $[64] = t28;
    } else {
      t28 = $[64];
    }
    let t29;
    if ($[68] !== rangeGroupLabel || $[69] !== t28) {
      t29 = <div role="group" aria-label={rangeGroupLabel} className="inline-flex gap-xs p-xs rounded-s bg-neutral-25">{t28}</div>;
      $[68] = rangeGroupLabel;
      $[69] = t28;
      $[70] = t29;
    } else {
      t29 = $[70];
    }
    let t30;
    if ($[71] !== view.change) {
      t30 = view.change && <span className="inline-flex items-center gap-2xs rounded-full border border-neutral-50 bg-neutral-0 px-3xs py-xs text-title-1 font-book text-neutral-425"><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="flex-none text-neutral-675"><path d={ARROWS[view.change.direction]} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg><span>{view.change.spokenDirection && <span className="sr-only">{view.change.spokenDirection}{" "}</span>}<span className="font-semibold text-neutral-675">{view.change.amount}</span>{" "}{view.change.since}</span></span>;
      $[71] = view.change;
      $[72] = t30;
    } else {
      t30 = $[72];
    }
    let t31;
    if ($[73] !== t30) {
      t31 = <span aria-live="polite">{t30}</span>;
      $[73] = t30;
      $[74] = t31;
    } else {
      t31 = $[74];
    }
    if ($[75] !== t29 || $[76] !== t31) {
      t26 = <div className="flex flex-wrap items-center justify-between gap-3xs">{t29}{t31}</div>;
      $[75] = t29;
      $[76] = t31;
      $[77] = t26;
    } else {
      t26 = $[77];
    }
    t24 = "flex flex-col flex-1 min-h-[200px] m:min-h-[240px]";
    t7 = "slider";
    t8 = 0;
    if ($[78] !== t) {
      t9 = t("rate_plot_label");
      $[78] = t;
      $[79] = t9;
    } else {
      t9 = $[79];
    }
    t10 = view.start;
    t11 = view.end;
    t12 = shown;
    t13 = t("rate_point", {
      rate: rate.format(shownRate),
      date: date.format(shown)
    });
    t14 = point;
    t15 = point;
    if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = () => setCursor(null);
      $[80] = t16;
    } else {
      t16 = $[80];
    }
    if ($[81] !== view.end) {
      t17 = () => setCursor(current => current ?? view.end);
      $[81] = view.end;
      $[82] = t17;
    } else {
      t17 = $[82];
    }
    if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = () => setCursor(null);
      $[83] = t18;
    } else {
      t18 = $[83];
    }
    t19 = step;
    t20 = "relative flex-1 rounded-xs focusable cursor-crosshair touch-pan-y";
    if ($[84] !== view.path) {
      t21 = <svg viewBox={`0 0 ${VIEWBOX.width} ${VIEWBOX.height}`} preserveAspectRatio="none" aria-hidden="true" className="absolute inset-none w-full h-full overflow-visible"><path d={view.path} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" className="text-primary-800" /></svg>;
      $[84] = view.path;
      $[85] = t21;
    } else {
      t21 = $[85];
    }
    const t32 = `absolute w-[9px] h-[9px] rounded-full bg-primary-800 ring-2 ${ringClass} -translate-x-1/2 -translate-y-1/2`;
    const t33 = `${view.endY}%`;
    let t34;
    if ($[86] !== t33) {
      t34 = {
        left: "100%",
        top: t33
      };
      $[86] = t33;
      $[87] = t34;
    } else {
      t34 = $[87];
    }
    if ($[88] !== t32 || $[89] !== t34) {
      t22 = <span aria-hidden="true" className={t32} style={t34} />;
      $[88] = t32;
      $[89] = t34;
      $[90] = t22;
    } else {
      t22 = $[90];
    }
    t23 = cursor !== null && <><span aria-hidden="true" className="absolute top-none bottom-none w-[1px] bg-neutral-150 pointer-events-none" style={{
        left: `${x}%`
      }} /><span aria-hidden="true" className={`absolute w-[9px] h-[9px] rounded-full bg-primary-800 ring-2 ${ringClass} -translate-x-1/2 -translate-y-1/2 pointer-events-none`} style={{
        left: `${x}%`,
        top: `${y}%`
      }} /><span aria-hidden="true" data-testid="rate-tooltip" className="absolute z-10 pointer-events-none rounded-s bg-neutral-675 px-3xs py-2xs shadow-3 whitespace-nowrap" style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-${x}%, ${y > 30 ? "calc(-100% - 12px)" : "12px"})`
      }}><span className="block text-body-2 font-semibold text-neutral-0">{rate.format(shownRate)} %</span><span className="block text-title-1 font-book text-neutral-100">{date.format(shown)}</span></span></>;
    $[17] = cursor;
    $[18] = date;
    $[19] = order;
    $[20] = point;
    $[21] = range;
    $[22] = rangeGroupLabel;
    $[23] = rate;
    $[24] = ringClass;
    $[25] = shown;
    $[26] = step;
    $[27] = t;
    $[28] = view.change;
    $[29] = view.end;
    $[30] = view.endY;
    $[31] = view.high;
    $[32] = view.low;
    $[33] = view.path;
    $[34] = view.points;
    $[35] = view.start;
    $[36] = views;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
    $[53] = t26;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
  } else {
    t10 = $[37];
    t11 = $[38];
    t12 = $[39];
    t13 = $[40];
    t14 = $[41];
    t15 = $[42];
    t16 = $[43];
    t17 = $[44];
    t18 = $[45];
    t19 = $[46];
    t20 = $[47];
    t21 = $[48];
    t22 = $[49];
    t23 = $[50];
    t24 = $[51];
    t25 = $[52];
    t26 = $[53];
    t7 = $[54];
    t8 = $[55];
    t9 = $[56];
  }
  let t27;
  if ($[91] !== t10 || $[92] !== t11 || $[93] !== t12 || $[94] !== t13 || $[95] !== t14 || $[96] !== t15 || $[97] !== t16 || $[98] !== t17 || $[99] !== t18 || $[100] !== t19 || $[101] !== t20 || $[102] !== t21 || $[103] !== t22 || $[104] !== t23 || $[105] !== t7 || $[106] !== t8 || $[107] !== t9) {
    t27 = <div role={t7} tabIndex={t8} aria-label={t9} aria-valuemin={t10} aria-valuemax={t11} aria-valuenow={t12} aria-valuetext={t13} onPointerDown={t14} onPointerMove={t15} onPointerLeave={t16} onFocus={t17} onBlur={t18} onKeyDown={t19} className={t20}>{t21}{t22}{t23}</div>;
    $[91] = t10;
    $[92] = t11;
    $[93] = t12;
    $[94] = t13;
    $[95] = t14;
    $[96] = t15;
    $[97] = t16;
    $[98] = t17;
    $[99] = t18;
    $[100] = t19;
    $[101] = t20;
    $[102] = t21;
    $[103] = t22;
    $[104] = t23;
    $[105] = t7;
    $[106] = t8;
    $[107] = t9;
    $[108] = t27;
  } else {
    t27 = $[108];
  }
  let t28;
  if ($[109] !== view.ticks) {
    t28 = view.ticks.map(_temp);
    $[109] = view.ticks;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== t28) {
    t29 = <div className="relative h-[28px] border-t border-neutral-50 mt-2xs" aria-hidden="true">{t28}</div>;
    $[111] = t28;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  let t30;
  if ($[113] !== t24 || $[114] !== t27 || $[115] !== t29) {
    t30 = <div className={t24}>{t27}{t29}</div>;
    $[113] = t24;
    $[114] = t27;
    $[115] = t29;
    $[116] = t30;
  } else {
    t30 = $[116];
  }
  let t31;
  if ($[117] !== caption) {
    t31 = <p className="text-title-1 font-book text-neutral-425 m-none">{caption}</p>;
    $[117] = caption;
    $[118] = t31;
  } else {
    t31 = $[118];
  }
  let t32;
  if ($[119] !== link) {
    t32 = link?.href && <a href={link.href} data-tracking-event={link.trackingId || undefined} className="text-body-2 font-semibold text-primary-800 hover:text-primary-825 no-underline focusable">{link.label}</a>;
    $[119] = link;
    $[120] = t32;
  } else {
    t32 = $[120];
  }
  let t33;
  if ($[121] !== t31 || $[122] !== t32) {
    t33 = <div className="flex flex-wrap items-baseline justify-between gap-2xs">{t31}{t32}</div>;
    $[121] = t31;
    $[122] = t32;
    $[123] = t33;
  } else {
    t33 = $[123];
  }
  let t34;
  if ($[124] !== t25 || $[125] !== t26 || $[126] !== t30 || $[127] !== t33) {
    t34 = <div className={t25}>{t26}{t30}{t33}</div>;
    $[124] = t25;
    $[125] = t26;
    $[126] = t30;
    $[127] = t33;
    $[128] = t34;
  } else {
    t34 = $[128];
  }
  return t34;
};
export default RatePlot;
function _temp(tick) {
  return <span key={`${tick.position}-${tick.label}`} className="absolute top-2xs text-title-1 font-book text-neutral-425 whitespace-nowrap" style={{
    left: `${tick.position}%`,
    transform: `translateX(${tickShift(tick.position)})`
  }}>{tick.label}</span>;
}